import { databaseManager } from './DatabaseManager';
import type {
  LocalJourney,
  LocalJourneyWeek,
  LocalJourneyActivity,
  LocalActivityProgress,
  LocalSyncLog,
} from './schema';
import type {
  Journey,
  JourneyWeek,
  JourneyActivity,
  ActivityProgress,
} from '@coffeeplexity/shared';

/**
 * Repository for offline journey data operations
 *
 * Provides CRUD operations for journey data with sync tracking.
 * Handles conversion between local SQLite format and API format.
 */
export class JourneyRepository {
  private get db() {
    return databaseManager.getDatabase();
  }

  // ============================================================================
  // JOURNEY OPERATIONS
  // ============================================================================

  /**
   * Get all journeys for a user
   */
  async getJourneys(userId: string): Promise<Journey[]> {
    const rows = await this.db.getAllAsync(
      'SELECT * FROM journeys WHERE user_id = ? ORDER BY created_at DESC',
      [userId]
    );
    return rows.map(this.mapLocalJourneyToJourney);
  }

  /**
   * Get a specific journey with all its weeks and activities
   */
  async getJourney(journeyId: string): Promise<Journey | null> {
    const journeyRow = await this.db.getFirstAsync(
      'SELECT * FROM journeys WHERE id = ?',
      [journeyId]
    );

    if (!journeyRow) {
      return null;
    }

    const journey = this.mapLocalJourneyToJourney(journeyRow as LocalJourney);

    // Load weeks
    const weekRows = await this.db.getAllAsync(
      'SELECT * FROM journey_weeks WHERE journey_id = ? ORDER BY week_number',
      [journeyId]
    );

    journey.weeks = [];
    for (const weekRow of weekRows) {
      const week = this.mapLocalWeekToWeek(weekRow as LocalJourneyWeek);

      // Load activities for this week
      const activityRows = await this.db.getAllAsync(
        'SELECT * FROM journey_activities WHERE week_id = ? ORDER BY order_index',
        [week.id]
      );

      week.activities = activityRows.map(row =>
        this.mapLocalActivityToActivity(row as LocalJourneyActivity)
      );

      journey.weeks.push(week);
    }

    return journey;
  }

  /**
   * Save a journey (insert or update)
   */
  async saveJourney(
    journey: Journey,
    needsSync: boolean = false
  ): Promise<void> {
    const localJourney = this.mapJourneyToLocalJourney(journey, needsSync);

    await this.db.runAsync(
      `
      INSERT OR REPLACE INTO journeys (
        id, user_id, title, description, skill_level, focus_areas,
        duration_weeks, time_commitment_hours_per_week, status, current_week,
        progress_percentage, learning_style, preferred_content_types,
        difficulty_preference, created_at, started_at, completed_at,
        last_activity_at, updated_at, last_sync_at, needs_sync, sync_version
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `,
      [
        localJourney.id,
        localJourney.user_id,
        localJourney.title,
        localJourney.description,
        localJourney.skill_level,
        localJourney.focus_areas,
        localJourney.duration_weeks,
        localJourney.time_commitment_hours_per_week,
        localJourney.status,
        localJourney.current_week,
        localJourney.progress_percentage,
        localJourney.learning_style,
        localJourney.preferred_content_types,
        localJourney.difficulty_preference,
        localJourney.created_at,
        localJourney.started_at,
        localJourney.completed_at,
        localJourney.last_activity_at,
        localJourney.updated_at,
        localJourney.last_sync_at,
        localJourney.needs_sync ? 1 : 0,
        localJourney.sync_version,
      ]
    );

    // Save weeks and activities
    for (const week of journey.weeks) {
      await this.saveJourneyWeek(week, needsSync);
    }
  }

  /**
   * Save a journey week
   */
  async saveJourneyWeek(
    week: JourneyWeek,
    needsSync: boolean = false
  ): Promise<void> {
    const localWeek = this.mapWeekToLocalWeek(week, needsSync);

    await this.db.runAsync(
      `
      INSERT OR REPLACE INTO journey_weeks (
        id, journey_id, week_number, title, description, learning_objectives,
        estimated_hours, is_unlocked, is_completed, completion_date,
        created_at, updated_at, last_sync_at, needs_sync, sync_version
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `,
      [
        localWeek.id,
        localWeek.journey_id,
        localWeek.week_number,
        localWeek.title,
        localWeek.description,
        localWeek.learning_objectives,
        localWeek.estimated_hours,
        localWeek.is_unlocked ? 1 : 0,
        localWeek.is_completed ? 1 : 0,
        localWeek.completion_date,
        localWeek.created_at,
        localWeek.updated_at,
        localWeek.last_sync_at,
        localWeek.needs_sync ? 1 : 0,
        localWeek.sync_version,
      ]
    );

    // Save activities
    for (const activity of week.activities) {
      await this.saveJourneyActivity(activity, needsSync);
    }
  }

  /**
   * Save a journey activity
   */
  async saveJourneyActivity(
    activity: JourneyActivity,
    needsSync: boolean = false
  ): Promise<void> {
    const localActivity = this.mapActivityToLocalActivity(activity, needsSync);

    await this.db.runAsync(
      `
      INSERT OR REPLACE INTO journey_activities (
        id, week_id, title, description, activity_type, registry_id,
        estimated_minutes, difficulty_level, required_equipment, order_index,
        prerequisites, is_completed, completion_date, user_rating,
        user_feedback, created_at, updated_at, last_sync_at, needs_sync, sync_version
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `,
      [
        localActivity.id,
        localActivity.week_id,
        localActivity.title,
        localActivity.description,
        localActivity.activity_type,
        localActivity.registry_id,
        localActivity.estimated_minutes,
        localActivity.difficulty_level,
        localActivity.required_equipment,
        localActivity.order_index,
        localActivity.prerequisites,
        localActivity.is_completed ? 1 : 0,
        localActivity.completion_date,
        localActivity.user_rating,
        localActivity.user_feedback,
        localActivity.created_at,
        localActivity.updated_at,
        localActivity.last_sync_at,
        localActivity.needs_sync ? 1 : 0,
        localActivity.sync_version,
      ]
    );
  }

  // ============================================================================
  // ACTIVITY PROGRESS OPERATIONS
  // ============================================================================

  /**
   * Get activity progress
   */
  async getActivityProgress(
    userId: string,
    activityId: string
  ): Promise<ActivityProgress | null> {
    const row = await this.db.getFirstAsync(
      'SELECT * FROM activity_progress WHERE user_id = ? AND activity_id = ?',
      [userId, activityId]
    );

    if (!row) {
      return null;
    }

    return this.mapLocalProgressToProgress(row as LocalActivityProgress);
  }

  /**
   * Save activity progress
   */
  async saveActivityProgress(
    progress: ActivityProgress,
    userId: string,
    needsSync: boolean = true,
    operationId?: string
  ): Promise<void> {
    const localProgress = this.mapProgressToLocalProgress(
      progress,
      userId,
      needsSync,
      operationId
    );

    await this.db.runAsync(
      `
      INSERT OR REPLACE INTO activity_progress (
        id, user_id, activity_id, status, progress_percentage, attempts,
        time_spent_minutes, success_rate, engagement_score, difficulty_rating,
        started_at, completed_at, last_accessed_at, last_sync_at, needs_sync,
        sync_version, operation_id
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
    `,
      [
        localProgress.id,
        localProgress.user_id,
        localProgress.activity_id,
        localProgress.status,
        localProgress.progress_percentage,
        localProgress.attempts,
        localProgress.time_spent_minutes,
        localProgress.success_rate,
        localProgress.engagement_score,
        localProgress.difficulty_rating,
        localProgress.started_at,
        localProgress.completed_at,
        localProgress.last_accessed_at,
        localProgress.last_sync_at,
        localProgress.needs_sync ? 1 : 0,
        localProgress.sync_version,
        localProgress.operation_id,
      ]
    );

    // Log sync operation if needed
    if (needsSync) {
      await this.logSyncOperation(
        'activity_progress',
        progress.id,
        'update',
        operationId
      );
    }
  }

  // ============================================================================
  // SYNC OPERATIONS
  // ============================================================================

  /**
   * Get all records that need syncing
   */
  async getRecordsNeedingSync(): Promise<{
    journeys: LocalJourney[];
    weeks: LocalJourneyWeek[];
    activities: LocalJourneyActivity[];
    progress: LocalActivityProgress[];
  }> {
    const [journeys, weeks, activities, progress] = await Promise.all([
      this.db.getAllAsync('SELECT * FROM journeys WHERE needs_sync = 1'),
      this.db.getAllAsync('SELECT * FROM journey_weeks WHERE needs_sync = 1'),
      this.db.getAllAsync(
        'SELECT * FROM journey_activities WHERE needs_sync = 1'
      ),
      this.db.getAllAsync(
        'SELECT * FROM activity_progress WHERE needs_sync = 1'
      ),
    ]);

    return {
      journeys: journeys as LocalJourney[],
      weeks: weeks as LocalJourneyWeek[],
      activities: activities as LocalJourneyActivity[],
      progress: progress as LocalActivityProgress[],
    };
  }

  /**
   * Mark record as synced
   */
  async markAsSynced(tableName: string, recordId: string): Promise<void> {
    await this.db.runAsync(
      `UPDATE ${tableName} SET needs_sync = 0, last_sync_at = ? WHERE id = ?`,
      [new Date().toISOString(), recordId]
    );
  }

  /**
   * Log sync operation
   */
  async logSyncOperation(
    tableName: string,
    recordId: string,
    operation: 'create' | 'update' | 'delete',
    operationId?: string
  ): Promise<void> {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    await this.db.runAsync(
      `
      INSERT INTO sync_log (
        id, table_name, record_id, operation, sync_status, retry_count,
        created_at, operation_id
      ) VALUES (?, ?, ?, ?, 'pending', 0, ?, ?)
    `,
      [
        id,
        tableName,
        recordId,
        operation,
        new Date().toISOString(),
        operationId,
      ]
    );
  }

  /**
   * Get pending sync operations
   */
  async getPendingSyncOperations(): Promise<LocalSyncLog[]> {
    const rows = await this.db.getAllAsync(
      "SELECT * FROM sync_log WHERE sync_status IN ('pending', 'failed') ORDER BY created_at"
    );
    return rows as LocalSyncLog[];
  }

  /**
   * Update sync operation status
   */
  async updateSyncOperationStatus(
    id: string,
    status: 'syncing' | 'completed' | 'failed',
    errorMessage?: string
  ): Promise<void> {
    await this.db.runAsync(
      `
      UPDATE sync_log 
      SET sync_status = ?, last_attempt_at = ?, error_message = ?, 
          retry_count = retry_count + 1
      WHERE id = ?
    `,
      [status, new Date().toISOString(), errorMessage, id]
    );
  }

  // ============================================================================
  // MAPPING FUNCTIONS
  // ============================================================================

  private mapLocalJourneyToJourney(local: LocalJourney): Journey {
    return {
      id: local.id,
      user_id: local.user_id,
      title: local.title,
      description: local.description,
      skill_level: local.skill_level,
      focus_areas: JSON.parse(local.focus_areas || '[]'),
      duration_weeks: local.duration_weeks,
      time_commitment_hours_per_week: local.time_commitment_hours_per_week,
      status: local.status,
      current_week: local.current_week,
      progress_percentage: local.progress_percentage,
      learning_style: local.learning_style,
      preferred_content_types: JSON.parse(
        local.preferred_content_types || '[]'
      ),
      difficulty_preference: local.difficulty_preference,
      created_at: local.created_at,
      started_at: local.started_at,
      completed_at: local.completed_at,
      last_activity_at: local.last_activity_at,
      updated_at: local.updated_at,
      weeks: [], // Will be populated separately
    };
  }

  private mapJourneyToLocalJourney(
    journey: Journey,
    needsSync: boolean
  ): LocalJourney {
    return {
      id: journey.id,
      user_id: journey.user_id,
      title: journey.title,
      description: journey.description,
      skill_level: journey.skill_level,
      focus_areas: JSON.stringify(journey.focus_areas || []),
      duration_weeks: journey.duration_weeks,
      time_commitment_hours_per_week: journey.time_commitment_hours_per_week,
      status: journey.status,
      current_week: journey.current_week,
      progress_percentage: journey.progress_percentage,
      learning_style: journey.learning_style,
      preferred_content_types: JSON.stringify(
        journey.preferred_content_types || []
      ),
      difficulty_preference: journey.difficulty_preference,
      created_at: journey.created_at,
      started_at: journey.started_at,
      completed_at: journey.completed_at,
      last_activity_at: journey.last_activity_at,
      updated_at: journey.updated_at,
      last_sync_at: needsSync ? undefined : new Date().toISOString(),
      needs_sync: needsSync,
      sync_version: 1,
    };
  }

  private mapLocalWeekToWeek(local: LocalJourneyWeek): JourneyWeek {
    return {
      id: local.id,
      journey_id: local.journey_id,
      week_number: local.week_number,
      title: local.title,
      description: local.description,
      learning_objectives: JSON.parse(local.learning_objectives || '[]'),
      estimated_hours: local.estimated_hours,
      is_unlocked: Boolean(local.is_unlocked),
      is_completed: Boolean(local.is_completed),
      completion_date: local.completion_date,
      created_at: local.created_at,
      updated_at: local.updated_at,
      activities: [], // Will be populated separately
    };
  }

  private mapWeekToLocalWeek(
    week: JourneyWeek,
    needsSync: boolean
  ): LocalJourneyWeek {
    return {
      id: week.id,
      journey_id: week.journey_id,
      week_number: week.week_number,
      title: week.title,
      description: week.description,
      learning_objectives: JSON.stringify(week.learning_objectives || []),
      estimated_hours: week.estimated_hours,
      is_unlocked: week.is_unlocked,
      is_completed: week.is_completed,
      completion_date: week.completion_date,
      created_at: week.created_at,
      updated_at: week.updated_at,
      last_sync_at: needsSync ? undefined : new Date().toISOString(),
      needs_sync: needsSync,
      sync_version: 1,
    };
  }

  private mapLocalActivityToActivity(
    local: LocalJourneyActivity
  ): JourneyActivity {
    return {
      id: local.id,
      week_id: local.week_id,
      title: local.title,
      description: local.description,
      activity_type: local.activity_type,
      registry_id: local.registry_id,
      estimated_minutes: local.estimated_minutes,
      difficulty_level: local.difficulty_level,
      required_equipment: JSON.parse(local.required_equipment || '[]'),
      order_index: local.order_index,
      prerequisites: JSON.parse(local.prerequisites || '[]'),
      is_completed: Boolean(local.is_completed),
      completion_date: local.completion_date,
      user_rating: local.user_rating,
      user_feedback: local.user_feedback,
      created_at: local.created_at,
      updated_at: local.updated_at,
      progress: undefined, // Will be loaded separately if needed
      content: undefined, // Will be loaded separately if needed
    };
  }

  private mapActivityToLocalActivity(
    activity: JourneyActivity,
    needsSync: boolean
  ): LocalJourneyActivity {
    return {
      id: activity.id,
      week_id: activity.week_id,
      title: activity.title,
      description: activity.description,
      activity_type: activity.activity_type,
      registry_id: activity.registry_id,
      estimated_minutes: activity.estimated_minutes,
      difficulty_level: activity.difficulty_level,
      required_equipment: JSON.stringify(activity.required_equipment || []),
      order_index: activity.order_index,
      prerequisites: JSON.stringify(activity.prerequisites || []),
      is_completed: activity.is_completed,
      completion_date: activity.completion_date,
      user_rating: activity.user_rating,
      user_feedback: activity.user_feedback,
      created_at: activity.created_at,
      updated_at: activity.updated_at,
      last_sync_at: needsSync ? undefined : new Date().toISOString(),
      needs_sync: needsSync,
      sync_version: 1,
    };
  }

  private mapLocalProgressToProgress(
    local: LocalActivityProgress
  ): ActivityProgress {
    return {
      id: local.id,
      user_id: local.user_id,
      activity_id: local.activity_id,
      status: local.status,
      progress_percentage: local.progress_percentage,
      attempts: local.attempts,
      time_spent_minutes: local.time_spent_minutes,
      success_rate: local.success_rate,
      engagement_score: local.engagement_score,
      difficulty_rating: local.difficulty_rating,
      started_at: local.started_at,
      completed_at: local.completed_at,
      last_accessed_at: local.last_accessed_at,
    };
  }

  private mapProgressToLocalProgress(
    progress: ActivityProgress,
    userId: string,
    needsSync: boolean,
    operationId?: string
  ): LocalActivityProgress {
    return {
      id: progress.id,
      user_id: userId,
      activity_id: progress.activity_id,
      status: progress.status,
      progress_percentage: progress.progress_percentage,
      attempts: progress.attempts,
      time_spent_minutes: progress.time_spent_minutes,
      success_rate: progress.success_rate,
      engagement_score: progress.engagement_score,
      difficulty_rating: progress.difficulty_rating,
      started_at: progress.started_at,
      completed_at: progress.completed_at,
      last_accessed_at: progress.last_accessed_at,
      last_sync_at: needsSync ? undefined : new Date().toISOString(),
      needs_sync: needsSync,
      sync_version: 1,
      operation_id: operationId,
    };
  }
}

// Export singleton instance
export const journeyRepository = new JourneyRepository();
